import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import MasonryGallery from '../components/MasonryGallery';
import imageSizes from '../lib/exhibitionImageSizes.json';
import { findExhibition, exhibitionNeighbours } from '../lib/data';

const ExhibitionDetailPage = () => {
    const { id } = useParams();
    const ex = findExhibition(id);
    if (!ex) return <Navigate to="/exhibitions" replace />;
    const { prev, next } = exhibitionNeighbours(id);
    const isCurrent = ex.status === 'On view' || ex.status === 'Closing soon';

    return (
        <div className="bg-bone text-charcoal" data-testid="exhibition-detail-page">
            <Header />

            {/* Top meta + breadcrumb */}
            <section className="pt-32 md:pt-40 px-6 md:px-12 lg:px-16">
                <div className="flex items-center justify-between text-[11px] tracking-eyebrow uppercase text-charcoal/55">
                    <nav className="flex items-center gap-2">
                        <Link to="/" className="hover:opacity-60 transition-opacity">Home</Link>
                        <span>/</span>
                        <Link to="/exhibitions" className="hover:opacity-60 transition-opacity">Exhibitions</Link>
                        <span>/</span>
                        <span className="text-charcoal/80 truncate max-w-[180px] md:max-w-none">
                            {ex.title}
                        </span>
                    </nav>
                    <span className="hidden md:inline">{ex.number} · {ex.year}</span>
                </div>
            </section>

            {/* Hero image */}
            <section className="px-6 md:px-12 lg:px-16 pt-8 md:pt-12">
                <Reveal>
                    <div className={`relative w-full overflow-hidden bg-beige ${ex.imageFit === 'contain' ? '' : 'aspect-[16/9] md:aspect-[16/8]'}`} style={ex.imageFit === 'contain' ? { aspectRatio: ex.imageAspect || '41 / 18' } : undefined}>
                        <motion.img
                            src={ex.image}
                            alt={ex.title}
                            initial={{ scale: ex.imageFit === 'contain' ? 1 : 1.08 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
                            className={`absolute inset-0 w-full h-full ${ex.imageFit === 'contain' ? 'object-contain' : 'object-cover'}`}
                        />
                        {ex.imageFit !== 'contain' && <span className="absolute top-5 left-5 text-[10px] tracking-eyebrow uppercase text-bone bg-charcoal px-3 py-1.5">
                            {isCurrent ? ex.status : 'Past exhibition'}
                        </span>}
                    </div>
                </Reveal>
            </section>

            {/* Title block */}
            <section className="px-6 md:px-12 lg:px-16 pt-14 md:pt-20 pb-12 md:pb-16">
                <div className="grid grid-cols-12 gap-x-6 gap-y-8">
                    <Reveal className="col-span-12 md:col-span-8" delay={0.05}>
                        <h1
                            className="font-sans font-light text-charcoal leading-[0.92] tracking-normal text-5xl sm:text-6xl md:text-7xl lg:text-[7vw] uppercase"
                            data-testid="exhibition-title"
                        >
                            {ex.title}
                        </h1>
                        {ex.artist && (
                            <p
                                className="mt-4 md:mt-6 font-sans font-light text-charcoal/80 text-2xl md:text-3xl lg:text-4xl"
                                data-testid="exhibition-artist"
                            >
                                {ex.artist === 'Group exhibition' ? 'Group exhibition' : `by ${ex.artist}`}
                            </p>
                        )}
                        <p className="mt-6 md:mt-8 text-[11px] tracking-eyebrow uppercase text-charcoal/60">
                            Curated by {ex.curator} · {ex.dates}
                        </p>
                    </Reveal>

                    <Reveal className="col-span-12 md:col-span-4 md:pt-10" delay={0.15}>
                        <dl className="grid grid-cols-2 md:grid-cols-1 gap-y-5 text-[11px] tracking-eyebrow uppercase">
                            <div>
                                <dt className="text-charcoal/45">Status</dt>
                                <dd className="mt-1 text-charcoal/85">{ex.status}</dd>
                            </div>
                            <div>
                                <dt className="text-charcoal/45">Location</dt>
                                <dd className="mt-1 text-charcoal/85">{ex.location}</dd>
                            </div>
                            <div>
                                <dt className="text-charcoal/45">Dates</dt>
                                <dd className="mt-1 text-charcoal/85">{ex.dates}</dd>
                            </div>
                            <div>
                                <dt className="text-charcoal/45">Reference</dt>
                                <dd className="mt-1 text-charcoal/85">DIM/{ex.number}.{ex.year}</dd>
                            </div>
                        </dl>
                    </Reveal>
                </div>
            </section>

            {/* Overview */}
            <section className="px-6 md:px-12 lg:px-16 pt-8 md:pt-16 pb-20 md:pb-28 border-t border-[#E6E4DD]">
                <div className="grid grid-cols-12 gap-x-6 gap-y-10 pt-14 md:pt-20">
                    <Reveal className="col-span-12 md:col-span-3">
                        <h2
                            className="font-sans font-light text-3xl md:text-4xl text-charcoal"
                            data-testid="overview-heading"
                        >
                            Overview
                        </h2>
                    </Reveal>
                    <div className="col-span-12 md:col-span-8 md:col-start-5 space-y-7">
                        {(ex.overview || [ex.description]).map((p, i) => (
                            <Reveal key={i} delay={0.1 + i * 0.06}>
                                <p
                                    className={`leading-relaxed font-light ${i === 0
                                            ? 'text-charcoal text-xl md:text-2xl font-sans font-light'
                                            : 'text-charcoal/80 text-base md:text-lg'
                                        }`}
                                >
                                    {p}
                                </p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Image gallery */}
            {ex.gallery && ex.gallery.length > 0 && (
                <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-32 border-t border-[#E6E4DD] pt-14 md:pt-20">
                    <Reveal>
                        <div className="flex items-baseline justify-between border-b border-[#E6E4DD] pb-4 mb-10 md:mb-14">
                            <h2
                                className="font-sans font-light text-3xl md:text-4xl text-charcoal"
                            >
                                Installation views
                            </h2>
                        </div>
                    </Reveal>
                    <MasonryGallery
                        images={ex.gallery.map(src => ({ src, ...imageSizes[src] }))}
                        label={`${ex.title} — installation view`}
                        testId="exhibition-gallery"
                        maxColumns={2}
                    />
                </section>
            )}

            {/* Related artist */}
            {ex.artist && ex.artist !== 'Group exhibition' && (
                <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-32 border-t border-[#E6E4DD] pt-16 md:pt-24">
                    <Reveal>
                        <span className="text-[11px] tracking-eyebrow uppercase text-charcoal/55">
                            Related artist
                        </span>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-6 md:mt-10 grid grid-cols-12 gap-x-6 gap-y-6 items-end">
                            <div className="col-span-4 md:col-span-2">
                                <div className="relative w-full aspect-square overflow-hidden bg-beige">
                                    <img
                                        src={ex.gallery?.[0] || ex.image}
                                        alt={ex.artist}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        style={{ filter: 'grayscale(0.6)' }}
                                    />
                                </div>
                            </div>
                            <div className="col-span-8 md:col-span-10">
                                <h3
                                    className="font-sans font-light text-4xl md:text-5xl text-charcoal leading-tight"
                                >
                                    {ex.artist}
                                </h3>
                                <p className="mt-2 text-[11px] tracking-eyebrow uppercase text-charcoal/55">
                                    Featured in {ex.title} · {ex.year}
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </section>
            )}

            {/* Prev / Next */}
            <section className="border-t border-[#E6E4DD]">
                <div className="grid grid-cols-2">
                    <NeighbourLink ex={prev} direction="prev" />
                    <NeighbourLink ex={next} direction="next" />
                </div>
            </section>

            <Footer />
        </div>
    );
};

const NeighbourLink = ({ ex, direction }) => {
    const isNext = direction === 'next';
    if (!ex)
        return (
            <div
                className={`px-6 md:px-12 lg:px-16 py-10 md:py-14 ${isNext ? 'text-right border-l border-[#E6E4DD]' : ''
                    } text-charcoal/35`}
            >
                <span className="text-[11px] tracking-eyebrow uppercase">
                    {isNext ? 'End of archive' : 'Beginning of archive'}
                </span>
            </div>
        );

    return (
        <Link
            to={`/exhibitions/${ex.id}`}
            data-testid={`neighbour-${direction}`}
            className={`group px-6 md:px-12 lg:px-16 py-10 md:py-14 ${isNext ? 'text-right border-l border-[#E6E4DD]' : ''
                } block hover:bg-beige/40 transition-colors duration-500`}
        >
            <span className="text-[11px] tracking-eyebrow uppercase text-charcoal/55">
                {isNext ? 'Next exhibition →' : '← Previous'}
            </span>
            <div
                className="mt-3 font-sans font-light text-3xl md:text-4xl lg:text-5xl leading-tight text-charcoal group-hover:opacity-70 transition-opacity duration-500"
            >
                {ex.title}
            </div>
            <div className="mt-2 text-[11px] tracking-eyebrow uppercase text-charcoal/50">
                {ex.dateShort || ex.dates} · {ex.location}
            </div>
        </Link>
    );
};

export default ExhibitionDetailPage;
